import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { formatBoxTooltip, toBoxGeometry } from '../../helpers/boxGeometry';
import type { ImageBox, ImageSize } from '../../types';
import type { BoxTooltipStyle } from '../../variants';

import Box from './Box';

import styles from './BoxOverlay.module.scss';

export interface BoxOverlayProps {
    bboxes?: ImageBox[];
    /** Pixel dimensions of the original image; the frame the bboxes are relative to. */
    dimensions: ImageSize;
    /** Called with the layer's own rect whenever it is (re)attached, to size the magnifier. */
    onLayerMeasured?: (rect: DOMRect) => void;
    isColor?: boolean;
    showPoiMeta?: boolean;
    showPoiTag?: boolean;
    show: boolean;
    tooltipStyle: BoxTooltipStyle;
    isOptoScaleAdmin?: boolean;
}

/**
 * The transparent layer of detection boxes sitting on top of the image.
 *
 * The boxes are rendered from the geometry, full stop. Previously this component rendered
 * them and then went back over the DOM in a ref callback: it ran `querySelectorAll('div')`,
 * paired the resulting NodeList against the geometry array *by index*, and used that
 * pairing to set `data-tag`/`data-type` and to read each box's tooltip back out of a CSS
 * custom property. The pairing only worked because Box happened to render exactly one div
 * with no children, and it threw outright if the DOM ever held more divs than boxes.
 *
 * All of that work was also thrown away: the callback measured every box with
 * getBoundingClientRect and handed the results to `poiPositionHandler`, whose only
 * implementation ignored its first argument and wanted just the layer's own rect. And
 * because that handler was re-created inline on every render of the container, this
 * callback's identity changed every render, so React detached and re-attached the ref -
 * re-running the whole loop - on every single render.
 *
 * What remains is `onLayerMeasured`, which is the part that was load-bearing: the container
 * turns the layer's rect into `--mlayer-width`/`--mlayer-height`, and the magnifier's CSS
 * clamps its own width against them.
 */
export default function BoxOverlay(props: BoxOverlayProps) {
    const {
        bboxes,
        dimensions,
        onLayerMeasured,
        isColor = true,
        showPoiMeta = false,
        showPoiTag = true,
        show = true,
        tooltipStyle,
        isOptoScaleAdmin = false,
    } = props;

    const { t, i18n } = useTranslation();

    const boxes = useMemo(
        () => (bboxes ?? []).map((box) => toBoxGeometry(box, dimensions)),
        [bboxes, dimensions]
    );

    const layerRef = useRef<HTMLDivElement>(null);

    /* Reports the layer's size once on mount and again whenever it changes. It has to keep
       reporting: the magnifier's width is clamped against --mlayer-width, so a stale value
       after a window resize or a fullscreen transition puts the magnifier at the wrong size
       in the wrong place.

       This used to fall out of an accident. The measurement lived in a ref callback whose
       dependency was a handler the container re-created inline on every render, so React
       detached and re-attached the ref every render and the value happened to stay fresh -
       at the cost of re-running a querySelectorAll and a getBoundingClientRect per box,
       every render. */
    useEffect(() => {
        const node = layerRef.current;

        if (!node || !onLayerMeasured) {
            return;
        }

        onLayerMeasured(node.getBoundingClientRect());

        // jsdom has no ResizeObserver; the initial measurement above still runs there.
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            onLayerMeasured(node.getBoundingClientRect());
        });
        observer.observe(node);

        return () => observer.disconnect();
    }, [onLayerMeasured]);

    return (
        <div className={classNames(styles.boxoverlay, show ? styles.show : '')} ref={layerRef}>
            {showPoiMeta &&
                boxes.map((geometry, index) => (
                    <Box
                        // Keyed on tag and position rather than a fresh uuid per render,
                        // which remounted every box whenever the layer re-rendered.
                        key={`${geometry.tag}-${geometry.left}-${geometry.top}-${index}`}
                        geometry={geometry}
                        showTag={showPoiTag}
                        toolTip={
                            isColor
                                ? formatBoxTooltip({
                                      box: geometry,
                                      style: tooltipStyle,
                                      t,
                                      locale: i18n.language,
                                      isOptoScaleAdmin,
                                  })
                                : ''
                        }
                    />
                ))}
        </div>
    );
}
